/** Reusable, permission-aware item reference with local stock context. */
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/app/auth";
import { useItem, type WarehouseStockRow } from "@/features/inventory/queries";
import { EntityPreview } from "@/shared/components/entity-preview";
import { ErrorState, LoadingState } from "@/shared/components/page";
import { formatQuantity } from "@/shared/lib/format";

/** Render an on-demand item overview without exposing unauthorized cost fields. */
function ItemOverview({ id, stock }: { id: string; stock?: WarehouseStockRow }): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const item = useItem(id);
  if (item.isPending) return <LoadingState />;
  if (item.isError) return <ErrorState error={item.error} />;
  return (
    <div className="space-y-3">
      <h3 className="font-semibold">
        {item.data.sku} · {item.data.name}
      </h3>
      <p className="text-sm">{item.data.description}</p>
      {can("inventory.stock.view") ? (
        <p className="text-sm">
          {t("warehouse.itemWideStock")}: {formatQuantity(item.data.total_on_hand)} {item.data.uom}
        </p>
      ) : null}
      {stock && can("inventory.stock.view") ? (
        <div className="rounded border p-2 text-sm">
          <p>
            {t("warehouse.location")}: {stock.location_path}
          </p>
          <p>
            {t("warehouse.onHand")}: {formatQuantity(stock.on_hand)}
          </p>
          <p>
            {t("warehouse.available")}: {formatQuantity(stock.available)}
          </p>
        </div>
      ) : null}
      {can("workorders.cost.view") ? (
        <p className="text-sm">
          {t("inventory.unitCost")}: {formatQuantity(item.data.standard_cost)}
        </p>
      ) : null}
      <Link to="/inventory/$itemId" params={{ itemId: id }} className="text-sm underline">
        {t("warehouse.openItem")}
      </Link>
      {can("inventory.stock.view") ? (
        <Link
          to="/warehouse"
          search={{ item_id: id, location_id: stock?.location_id }}
          className="ms-3 text-sm underline"
        >
          {t("warehouse.stock")}
        </Link>
      ) : null}
    </div>
  );
}

/** Linked item name with mouse, keyboard and touch overview controls. */
export function ItemName(
  props: {
    name: string;
    sku?: string;
    stock?: WarehouseStockRow;
  } & ({ itemId: string } | { id: string }),
): ReactElement {
  const { name, sku, stock } = props;
  const id = "itemId" in props ? props.itemId : props.id;
  const { can } = useAuth();
  const code = sku === name ? undefined : sku;
  const label = code ? `${code} · ${name}` : name;
  const content = (
    <>
      {code ? <span className="font-mono text-xs">{code}</span> : null}
      {code ? " " : null}
      <span>{name}</span>
    </>
  );
  if (!can("inventory.item.view")) return <span>{content}</span>;
  return (
    <EntityPreview label={label} preview={() => <ItemOverview id={id} stock={stock} />}>
      <Link to="/inventory/$itemId" params={{ itemId: id }} className="hover:underline">
        {content}
      </Link>
    </EntityPreview>
  );
}

/** Reusable item reference, including its permission-aware overview. */
export const ItemReference = ItemName;
